import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { PhoneOff, CreditCard, Banknote, AlertTriangle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { DeepOrder } from '../types'

/**
 * Pick the truthful refund line for the given paymentStatus.
 * Backend (AdminOrdersController.cancelOrder) only auto-refunds `stripe_success`.
 * All other payment methods need manual action — the dialog must say so.
 */
function getRefundInfo(paymentStatus: string): { key: string; icon: LucideIcon } {
  switch (paymentStatus.toLowerCase()) {
    case 'stripe_success':
      return { key: 'cancel.refundStripe', icon: CreditCard }
    case 'paypal_success':
    case 'paypalme_new':
      return { key: 'cancel.refundPayPal', icon: AlertTriangle }
    case 'wirecard_success':
    case 'cc_success':
      return { key: 'cancel.refundManualOnline', icon: AlertTriangle }
    case 'cash':
      return { key: 'cancel.refundCash', icon: Banknote }
    default:
      return { key: 'cancel.refundUnpaid', icon: Banknote }
  }
}

interface CancelOrderDialogProps {
  order: DeepOrder | null
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason?: string) => void
  isLoading?: boolean
}

export function CancelOrderDialog({
  order,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: CancelOrderDialogProps) {
  const { t } = useTranslation('orders')
  const [reason, setReason] = useState('')

  const handleConfirm = () => {
    onConfirm(reason.trim() || undefined)
    setReason('')
  }

  const handleClose = () => {
    setReason('')
    onClose()
  }

  if (!order) return null

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('cancel.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('cancel.description', { id: order.id })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* What happens section — payment-method aware, factually accurate */}
        {(() => {
          const refund = getRefundInfo(order.paymentStatus)
          const RefundIcon = refund.icon
          return (
            <div className="space-y-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p className="font-medium text-foreground">{t('cancel.whatHappens')}</p>
              <div className="flex items-start gap-2">
                <PhoneOff className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t('cancel.noNotification')}</span>
              </div>
              <div className="flex items-start gap-2">
                <RefundIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t(refund.key)}</span>
              </div>
            </div>
          )
        })()}

        <div className="py-2 tablet:py-3">
          <Label htmlFor="cancel-reason" className="text-sm tablet:text-kitchen-sm font-medium">
            {t('cancel.reason')}
          </Label>
          <Textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('cancel.reasonPlaceholder')}
            className="mt-2 tablet:mt-3 tablet:text-kitchen-sm tablet:min-h-[100px]"
            rows={3}
          />
        </div>

        {/* Safe action (keep) is more prominent than destructive (cancel) */}
        <AlertDialogFooter className="tablet:gap-3">
          <AlertDialogCancel
            onClick={handleClose}
            disabled={isLoading}
            className="tablet:min-h-touch tablet:text-kitchen-sm tablet:px-6"
          >
            {t('cancel.keep')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 tablet:min-h-touch tablet:text-kitchen-sm tablet:px-6"
          >
            {t('cancel.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
